import { Close } from '@styled-icons/evil/Close';
import { Link } from 'shared-routing';
import { media } from 'styled-bootstrap-grid';
import styled, { createGlobalStyle } from 'styled-components';
import { fonts, primitives, size, space } from 'voyager';
import { Button } from '../Button';
import ImgixWrapper from '../ImgixWrapper';
import { PageTitle } from '../Typography';
import { Stack } from '../Stack';

const flexStyles = `
  display: flex;
  flex: 1;
`;

export const BasketDrawerContainer = styled.div<{ show: boolean }>`
  position: relative;
  z-index: 10000;

  > div {
    transition: transform 300ms cubic-bezier(0.65, 0, 0.35, 1);
    transform: ${({ show }) => (show ? `translateX(0)` : `translateX(100%)`)};
  }
`;

export const BasketDrawerBase = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
`;

export const BasketDrawerFixed = styled(BasketDrawerBase)``;

export const BasketDrawerAbsolutePositioning = styled(BasketDrawerBase)`
  position: absolute;
  overflow: hidden;
`;

export const BasketDrawerPosition = styled(BasketDrawerBase)`
  inset: auto;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  max-width: 100%;
`;

export const Panel = styled.div`
  pointer-events: auto;
  max-width: 520px;
  min-width: 520px;

  ${media.xs`
    width: 100vw;
    min-width: 100vw;
  `}
`;

export const PanelBackground = styled.div`
  ${flexStyles}
  height: 100%;
  flex-direction: column;
  background-color: white;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 0 0 0 15px;

  ${media.sm`
    border-radius: 15px 0 0 15px;
  `}
`;

export const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  margin-bottom: 20px;
`;

export const BasketDrawerTitle = styled(PageTitle)`
  margin-bottom: 0;
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const CloseContainer = styled.div`
  margin: auto 0;
  display: flex;
  height: 1.75rem;
  align-items: center;
`;

export const StyledClose = styled(Close)`
  cursor: pointer;
  height: 2rem;
  width: 2rem;
`;

export const ContentContainer = styled.div`
  ${flexStyles}
  flex-direction: column;
  overflow: auto;
  padding: 30px;
`;

export const Listing = styled.div`
  ${flexStyles}
  flex-direction: column;
  grid-area: listing;
`;

const BaseText = styled.div`
  margin: 0;
  font-size: 16px;
  min-width: 100px;
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
`;

export const ItineraryToggle = styled(BaseText)`
  color: #c89384;
  font-family: 'Nanum Gothic', sans-serif;
  font-weight: 700;
  line-height: 22.4px;
  white-space: nowrap;
  align-self: center;
  cursor: pointer;
`;

export const Message = styled(BaseText)`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

export const TotalText = styled(BaseText).attrs({ as: 'p' })<{
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  marginLeft?: string;
}>`
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  display: inline;
`;

const BaseButton = styled(Button)`
  max-width: fit-content;
  display: inline-block;
  white-space: nowrap;
  padding: 8px 16px;
`;

export const StyledButton = styled(Button)`
  white-space: nowrap;
  width: 100%;
  font-size: 14px;
  ${media.md`
    font-size: 16px;
  `}
`;

export const ContinueBrowsingButton = styled.button`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space['space-100']};
  width: 100%;
  height: 40px;
  padding: ${space['space-100']} ${space['space-400']};
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.bodyFont};
  font-size: ${fonts.size.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1.5;
  color: ${({ theme }) => theme.defaultColors.fontPrimary};
`;

export const ContinueBrowsingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${primitives.size['size-600']};
  height: ${primitives.size['size-600']};
`;

export const StyledButtonRecommendations = styled(BaseButton)``;

export const StyledRecommendationButton = styled(BaseButton)``;

export const BasketOverview = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.greyColors['grey-20']};
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

export const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.greyColors['grey-20']};
  margin: 0 30px;
`;

export const GlobalStyle = createGlobalStyle`
  body {
    overflow: hidden;
    touch-action: none;
    overscroll-behavior: none;
  }
`;

export const BasketDrawItemGroupTitle = styled.h4`
  min-width: 100px;
  margin: 0;
  padding-bottom: 15px;
  font-family: ${({ theme }) => theme.headerFont};
  color: ${({ theme }) => theme.primaryColour};
  font-weight: 500;
  font-size: 18px;
  ${media.md`
    font-size: 22px;
  `}
`;

export const BasketDrawerTextWrapper = styled.div<{
  padding?: string;
  marginBottom?: string;
  columns?: number;
}>`
  padding: ${({ padding }) => padding || '0'};
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
  line-height: 23px;
  font-size: 16px;
  margin-bottom: ${({ marginBottom }) => marginBottom || '0'};
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  width: 95%;
`;

export const BasketDrawerTextItem = styled.div`
  flex: 1 0 0;
  min-width: 0;

  &:not(:last-child) {
    max-width: fit-content;
  }
`;

export const PackageStack = styled(Stack)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ gap }) => gap};
  border-bottom: 1px solid ${({ theme }) => theme.greyColors['grey-20']};
  padding-bottom: 10px;
  margin-bottom: 20px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  > * {
    &:first-child {
      flex: 1 1 100%;
    }

    &:last-child {
      flex: 1 1 100%;
      border-bottom: none;
    }
  }

  ${media.md`
    flex-wrap: nowrap;
    
    > * {
      &:first-child {
        flex: 0 0 auto;
      }
      
      &:last-child {
        flex: 1;
        border-bottom: none;
      }
    }
  `}
`;

export const GuestSelectContainer = styled.div`
  max-width: 300px;
`;

export const BasketItemText = styled.span`
  font-size: 16px;
  max-width: 100%;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const BasketBannerContainer = styled.div`
  padding-bottom: 30px;
`;

export const StickyContainer = styled.div`
  justify-content: space-between;
  flex-shrink: 0;
  background-color: white;
  border-bottom-left-radius: 0;

  ${media.sm`
    border-bottom-left-radius: 15px;
  `}
`;

export const BasketTextAndView = styled.div`
  display: flex;
  padding: 0 30px;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

export const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  padding: 30px;
  ${media.sm`
    grid-template-columns: auto auto;
  `}
`;

export const ItemText = styled.p<{ marginLeft?: string }>`
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  display: inline;
  color: ${({ theme }) => theme.defaultColors.fontSecondary};
  font-size: 16px;
`;

export const StyledPrice = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.defaultColors.fontPrimary};
  font-size: 16px;
`;

export const PackageText = styled.h4<{
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  marginLeft?: string;
}>`
  grid-area: title;
  margin: 0;
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  min-width: 100px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.defaultColors.fontPrimary};
  font-size: 16px;
`;

export const ItineraryItem = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.greyColors['grey-20']};
`;
export const ItineraryGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  gap: 10px;
`;

export const ItineraryStack = styled(Stack)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ gap }) => gap};
  border-bottom: 1px solid ${({ theme }) => theme.greyColors['grey-20']};
  padding: 10px 0;
  width: 100%;

  &:first-of-type {
    padding-top: 0;
  }
`;

export const ItineraryPrimaryText = styled.h4<{
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  marginLeft?: string;
  marginBottom?: string;
  marginTop?: string;
}>`
  grid-area: title;
  margin: 0;
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  min-width: 100px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme, color }) => color ?? theme.defaultColors.fontPrimary};
  font-size: 16px;
  margin-bottom: ${({ marginBottom }) => marginBottom ?? '0'};
  margin-top: ${({ marginTop }) => marginTop ?? '0'};
`;

export const ItinerarySecondaryText = styled.p<{
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  marginLeft?: string;
}>`
  grid-area: title;
  margin: 0;
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  min-width: 100px;
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  color: ${({ theme }) => theme.defaultColors.grey};
  font-size: 16px;
`;

export const PackageTitle = styled.h3`
  margin-bottom: 20px;
  font-family: ${({ theme }) => theme.headerFont};
  color: ${({ theme }) => theme.primaryColour};
  font-weight: 500;
  font-size: 18px;
  ${media.md`
    font-size: 22px;
  `}
  margin-top: 0;
`;

export const BasketItemListingWrapper = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.greyColors['grey-20']};
`;

export const BasketListingContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

export const FlexOne = styled.div`
  flex: 1;
`;

export const StyledImg = styled(ImgixWrapper)`
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`;

export const StyledLink = styled(Link)`
  font-size: 16px;
`;
